import {int} from "@opendaw/lib-std"
import {AudioUnitBox, CubedDeviceBox} from "@opendaw/studio-boxes"
import {CubedStep, InstrumentFactories} from "@opendaw/studio-adapters"
import {Project} from "@opendaw/studio-core"
import {AcidTrackSpec} from "@/generate/AcidTrack"
import {BassGenerator, BassOptions} from "@/generate/BassGenerator"
import {Motion} from "./Motion"

export class BassLine {
    static readonly UnitVolume = -6.0
    static readonly DeviceVolume = -12.0
    // The one knob the device holds a value for: its modulator is bipolar and swings +/- this much (Motion).
    static readonly Resonance = 0.75

    static create(project: Project, spec: AcidTrackSpec): BassLine {
        const {api, editing} = project
        const {instrumentBox, audioUnitBox} = editing.modify(() => {
            const {instrumentBox, audioUnitBox} = api.createInstrument(InstrumentFactories.Cubed, {name: "Line"})
            audioUnitBox.volume.setValue(BassLine.UnitVolume)
            // The knobs sit at zero, Motion's modulators own the whole dial. Resonance is the exception: it
            // rests high and its bipolar modulator swings around that.
            instrumentBox.cutoff.setValue(0.0)
            instrumentBox.resonance.setValue(BassLine.Resonance)
            instrumentBox.envMod.setValue(0.0)
            instrumentBox.decay.setValue(0.0)
            instrumentBox.accent.setValue(0.0)
            instrumentBox.volume.setValue(BassLine.DeviceVolume)
            return {instrumentBox, audioUnitBox}
        }).unwrap("bass-line")
        Motion.create(project, instrumentBox, spec.motion)
        const line = new BassLine(project, audioUnitBox, instrumentBox)
        // Written BEFORE the worklet starts so the bank travels in the engine's initial graph dump.
        line.write(spec.bass)
        return line
    }

    readonly #project: Project
    readonly #audioUnitBox: AudioUnitBox
    readonly #box: CubedDeviceBox

    #bank: int = 1

    constructor(project: Project, audioUnitBox: AudioUnitBox, box: CubedDeviceBox) {
        this.#project = project
        this.#audioUnitBox = audioUnitBox
        this.#box = box
    }

    get audioUnitBox(): AudioUnitBox {return this.#audioUnitBox}
    get box(): CubedDeviceBox {return this.#box}

    // The next variation in the bank, wrapping. The arranger calls this after a break.
    advance(): void {
        this.#project.editing.modify(() =>
            this.#box.patternIndex.setValue((this.#box.patternIndex.getValue() + 1) % this.#bank))
    }

    write(options: BassOptions): ReadonlyArray<ReadonlyArray<CubedStep>> {
        const bank = BassGenerator.generate(options)
        this.#bank = bank.length
        this.#project.editing.modify(() => {
            bank.forEach((steps, index) => {
                const pattern = this.#box.patterns.getField(index)
                pattern.length.setValue(steps.length)
                const fields = pattern.steps.fields()
                fields.forEach((field, step) => field.setValue(
                    step < steps.length ? CubedStep.pack(steps[step]) : CubedStep.pack({
                        note: CubedStep.DefaultNote, active: false, slide: false, accent: false
                    })))
            })
        })
        return bank
    }
}
